/**
 * Sticky Navigation Web Component
 * Provides scroll-based active link tracking and smooth scroll navigation
 */
class StickyNav extends HTMLElement {
  constructor() {
    super();
    this.navLinks = [];
    this.sections = [];
    this.ticking = false;
    this.headerHeight = 65;
    this.spacingOffset = 128;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    // Get all navigation links
    this.navLinks = Array.from(this.querySelectorAll('[data-nav-link]'));
    
    // Get section selector from attribute or default
    const sectionSelector = this.getAttribute('section-selector') || '[data-section-id]';
    this.sections = Array.from(document.querySelectorAll(sectionSelector));

    // Get configuration from attributes
    this.headerHeight = parseInt(this.getAttribute('header-height') || '65');
    this.spacingOffset = parseInt(this.getAttribute('spacing-offset') || '128');

    if (this.navLinks.length === 0 || this.sections.length === 0) {
      console.warn('StickyNav: No navigation links or sections found');
      return;
    }

    this.setupEventListeners();
    this.handleInitialHash();
  }

  setupEventListeners() {
    // Add click listeners to navigation links
    this.navLinks.forEach((link) => {
      link.addEventListener('click', (e) => this.handleLinkClick(e, link));
    });

    // Add scroll listener with throttling
    window.addEventListener('scroll', () => this.handleScroll(), { passive: true });

    // Handle hash changes
    window.addEventListener('hashchange', () => this.handleHashChange());
  }

  handleLinkClick(e, link) {
    e.preventDefault();
    
    const sectionId = link.getAttribute('data-section') || link.getAttribute('href').substring(1);
    const targetSection = document.getElementById(sectionId);

    if (targetSection) {
      this.scrollToSection(targetSection, sectionId);
    }
  }

  scrollToSection(targetSection, sectionId) {
    const isMobile = window.innerWidth <= 768;
    
    // On mobile: position content 128px from top of viewport
    // On desktop: use spacingOffset (128px) after header
    const totalOffset = isMobile 
      ? 128  // Fixed 128px from top on mobile
      : this.headerHeight + this.spacingOffset;
    
    const elementPosition = targetSection.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - totalOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });

    // Update URL hash
    history.pushState(null, null, '#' + sectionId);
  }

  handleScroll() {
    if (!this.ticking) {
      window.requestAnimationFrame(() => {
        this.updateActiveLink();
        this.ticking = false;
      });
      this.ticking = true;
    }
  }

  updateActiveLink() {
    if (this.sections.length === 0) return;

    const isMobile = window.innerWidth <= 768;
    const mobileNavHeight = isMobile ? 30 : 0;
    const mobileOffset = isMobile ? 64 : 0;
    const threshold = this.headerHeight + this.spacingOffset + mobileNavHeight + mobileOffset;
    let activeSection = null;
    let closestDistance = Infinity;

    // Find the section whose top is closest to threshold (that has passed it)
    this.sections.forEach((section) => {
      const sectionId = section.id;
      
      const rect = section.getBoundingClientRect();
      const elementTop = rect.top;

      // If element top is above or at threshold (element has scrolled into view)
      if (elementTop <= threshold) {
        const distance = Math.abs(elementTop - threshold);
        if (distance < closestDistance) {
          closestDistance = distance;
          activeSection = sectionId;
        }
      }
    });

    if (activeSection) {
      this.setActiveLink(activeSection);
    } else {
      // If no section is past threshold, activate the first link
      if (this.navLinks.length > 0) {
        this.navLinks.forEach((link) => link.classList.remove('active'));
        this.navLinks[0].classList.add('active');
      }
    }
  }

  setActiveLink(sectionId) {
    this.navLinks.forEach((link) => {
      const linkSection = link.getAttribute('data-section') || link.getAttribute('href').substring(1);
      if (linkSection === sectionId) {
        link.classList.add('active');
      } else {
        link.classList.remove('active');
      }
    });
  }

  handleInitialHash() {
    if (window.location.hash) {
      const hash = window.location.hash.substring(1);
      const targetSection = document.getElementById(hash);
      
      if (targetSection) {
        setTimeout(() => {
          this.scrollToSection(targetSection, hash);
          this.setActiveLink(hash);
        }, 100);
      }
    } else {
      this.updateActiveLink();
    }
  }

  handleHashChange() {
    const hash = window.location.hash.substring(1);
    const targetSection = document.getElementById(hash);
    
    if (targetSection) {
      this.scrollToSection(targetSection, hash);
      this.setActiveLink(hash);
    }
  }
}

// Register the custom element
customElements.define('sticky-nav', StickyNav);
